import { lazy } from 'react';
import { Navigate, createBrowserRouter } from 'react-router-dom';
import { AppShell } from '@/shell/AppShell';
import { AuthLayout } from '@/shell/AuthLayout';
import { RequirePermission } from '@/shell/RequirePermission';
import { LandingRedirect } from '@/shell/LandingRedirect';

import LoginRoute from '@/routes/login/LoginRoute';
import NoAccessRoute from '@/routes/NoAccessRoute';
/**
 * Every screen after the sign-in is a chunk of its own, fetched when it is first opened (`AppShell` shows a skeleton
 * meanwhile). The first load is budgeted (`scripts/check-bundle-size.mjs`) and an operator needs one screen first,
 * not all of them: with every route imported eagerly the first load grew by a screen at a time and the budget was
 * raised each time. Sign-in, the shell and the landing redirect stay in the first load.
 */
const MapRoute = lazy(() => import('@/routes/map/MapRoute'));
const DashboardRoute = lazy(() => import('@/routes/dashboard/DashboardRoute'));
const AnalyticsRoute = lazy(() => import('@/routes/analytics/AnalyticsRoute'));
const ZonesRoute = lazy(() => import('@/routes/zones/ZonesRoute'));
const AlertsRoute = lazy(() => import('@/routes/alerts/AlertsRoute'));
const ReportsRoute = lazy(() => import('@/routes/reports/ReportsRoute'));
const HealthRoute = lazy(() => import('@/routes/health/HealthRoute'));
const AdminDataRoute = lazy(() => import('@/routes/admin/data/AdminDataRoute'));
const AdminAccessRoute = lazy(() => import('@/routes/admin/access/AdminAccessRoute'));
const AdminAlertingRoute = lazy(() => import('@/routes/admin/alerting/AdminAlertingRoute'));
const ProfileRoute = lazy(() => import('@/routes/profile/ProfileRoute'));
const TokensRoute = lazy(() => import('@/routes/dev/TokensRoute'));
const StatesRoute = lazy(() => import('@/routes/dev/StatesRoute'));
const OpenItemsRoute = lazy(() => import('@/routes/dev/OpenItemsRoute'));

/**
 * Nine navigable routes plus `/login` and `/profile`.
 *
 * `/login` sits under its own layout rather than inside the shell — CHANGE-1,
 * a deliberate departure from figure 7.3 at UDC's request. A visitor who has
 * not authenticated has one thing to do, and a dimmed list of nine routes
 * they cannot open is not it.
 *
 * `/profile` is inside the shell but outside `RequirePermission`: it is the
 * caller's own account, and gating your own name behind a permission is how
 * a zero-permission user ends up unable to find out why.
 *
 * Every screen keeps its filter state in the URL, so a filtered view is a
 * shareable link (§9.11) — which is why routing is search-param-first and
 * `createBrowserRouter` rather than a hash router.
 */
export const router = createBrowserRouter([
  {
    element: <AuthLayout />,
    children: [{ path: '/login', element: <LoginRoute /> }],
  },
  {
    path: '/',
    element: <AppShell />,
    children: [
      { index: true, element: <LandingRedirect /> },
      { path: 'profile', element: <ProfileRoute /> },
      {
        path: 'map',
        element: (
          <RequirePermission anyOf={['bays.read']}>
            <MapRoute />
          </RequirePermission>
        ),
      },
      {
        path: 'dashboard',
        element: (
          <RequirePermission anyOf={['bays.read']}>
            <DashboardRoute />
          </RequirePermission>
        ),
      },
      {
        path: 'analytics',
        element: (
          <RequirePermission anyOf={['bays.read']}>
            <AnalyticsRoute />
          </RequirePermission>
        ),
      },
      {
        path: 'zones',
        element: (
          <RequirePermission anyOf={['zones.read']}>
            <ZonesRoute />
          </RequirePermission>
        ),
      },
      {
        // `/zones/{code}` is the readable identifier the plan and the figure
        // both use; the tab and the open bay ride alongside as search params.
        path: 'zones/:code',
        element: (
          <RequirePermission anyOf={['zones.read']}>
            <ZonesRoute />
          </RequirePermission>
        ),
      },
      {
        path: 'alerts',
        element: (
          <RequirePermission anyOf={['alerts.read']}>
            <AlertsRoute />
          </RequirePermission>
        ),
      },
      {
        path: 'reports',
        element: (
          <RequirePermission anyOf={['reports.read']}>
            <ReportsRoute />
          </RequirePermission>
        ),
      },
      {
        path: 'health',
        element: (
          <RequirePermission anyOf={['health.read']}>
            <HealthRoute />
          </RequirePermission>
        ),
      },
      {
        path: 'admin/data',
        element: (
          <RequirePermission
            anyOf={['bays.write', 'zones.write', 'devices.map', 'calibration.read', 'signs.write']}
          >
            <AdminDataRoute />
          </RequirePermission>
        ),
      },
      {
        path: 'admin/access',
        element: (
          <RequirePermission
            anyOf={['users.read', 'roles.manage', 'audit.read', 'integrations.manage']}
          >
            <AdminAccessRoute />
          </RequirePermission>
        ),
      },
      {
        path: 'admin/alerting',
        element: (
          <RequirePermission anyOf={['alerts.configure']}>
            <AdminAlertingRoute />
          </RequirePermission>
        ),
      },
      /**
       * Internal pages: the open-items register, the design tokens and the state gallery. They are for the
       * mock / demo build; a production build (`VITE_USE_MOCKS=false`) does not serve them.
       */
      ...(import.meta.env.VITE_USE_MOCKS !== 'false'
        ? [
            { path: 'admin/open-items', element: <OpenItemsRoute /> },
            { path: 'dev/tokens', element: <TokensRoute /> },
            { path: 'dev/states', element: <StatesRoute /> },
          ]
        : []),
      { path: 'no-access', element: <NoAccessRoute /> },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
]);
